import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm, useFormState } from "react-hook-form";
import { useEffect, useState } from "react";

// Types.
import EmailService from "../../../../types/integration/EmailService";
import IntegrationType from "../../../../types/integration/IntegrationType";

// Constants.
import { ACTIONS, attributesArray, generalErrorMessage } from '../../../../component/Constants';

//Utils
import { getAction, getValidClass } from "../../../../helpers/utils";
import Action from "../../../../types/Action";
import api from "../../../../api/Base";
import Alert from "../../../../component/Alert";
import ConditionalRender from "../../../../helpers/ConditionalRender";
import IntegrationsApi from "../../../../api/Integrations";
import useIntegrationTest from "../../../../hooks/useIntegrationTest";
import {
    findProfile,
    getNextProfilePrefix,
    getProfileValue,
    NOTIFICATION_INTEGRATIONS
} from "../../../../helpers/notificationIntegrations";

function Detail(props: { emailIntegration: IntegrationType[], refresh: Function, isAdmin: boolean }) {
    const { actionId, profileId } = useParams();
    const [action, setAction] = useState<Action>(ACTIONS.CREATE);
    const navigate = useNavigate();
    const config = NOTIFICATION_INTEGRATIONS.email;

    // Redirect non-admin users
    useEffect(() => {
        if (!props.isAdmin) {
            navigate('/admin/integrations/email/');
        }
    }, [props.isAdmin, navigate]);
    const [smtpPassElementType, setSmtpPassElementType] = useState<string>('password');
    const [visiblePassword, setVisiblePassword] = useState<boolean>(false);
    const [hasChanged, setHasChanged] = useState(false);

    // Handle input change
    const handleChange = (event: any) => {
        const newValue = event.nativeEvent.data
        if (!hasChanged) {
            setHasChanged(true);
            setVisiblePassword(true);
            event.target.value = newValue?.trim();
            setValue('password', newValue, { shouldDirty: true });
        }
    };

    const isDelete = action.type === ACTIONS.DELETE.type;
    const {
        register,
        trigger,
        handleSubmit,
        getValues,
        watch,
        setValue,
        control,
        clearErrors,
        formState: { errors }
    } = useForm<EmailService>({
        mode: "onBlur",
        reValidateMode: 'onBlur',
        shouldFocusError: true
    });

    useEffect(() => {
        setVisiblePassword(action === ACTIONS.CREATE)
    }, [action]);

    useEffect(() => {
        getAction(actionId, setAction, setDisabled);
    }, [actionId]);

    const [disabled, setDisabled] = useState(isDelete);

    const { TestNowButton, TestResultAlert } = useIntegrationTest({
        onTest: () => {
            const data = getValues();
            const params: { name: string; value: string; encrypted?: boolean }[] = [];
            Object.keys(data).forEach(item => {
                // Don't send credentials when authentication is disabled, so the
                // test e-mail is sent without attempting SMTP authentication.
                if (!data.auth && (item === 'user' || item === 'password')) {
                    return;
                }
                let name = item === 'ssl' ? 'mail.smtp.starttls.enable' : `mail.smtp.${item}`;
                name = item === 'url' ? 'mail.smtp.dbmarlin.url' : name;
                // @ts-ignore
                const value = data[item] === true ? 'true' : data[item] === false ? 'false' : data[item];
                const entry: { name: string; value: string; encrypted?: boolean } = { name, value };
                if (item === 'password') entry.encrypted = !hasChanged;
                params.push(entry);
            });
            return IntegrationsApi.testEmailIntegration(params);
        },
        successMessage: 'Test email sent successfully.',
        errorMessage: 'Test failed. Please check your email settings.',
    });

    const parseEditResponse = (data: any) => {
        if (action.type === ACTIONS.CREATE.type) {
            return;
        }
        const profile = findProfile(data, config, action.type === ACTIONS.CREATE.type ? undefined : profileId);
        if (profile) {
            setValue('name' as keyof EmailService, profile.label as any);
            attributesArray.forEach(attr => setValue(attr as keyof EmailService, getProfileValue(profile, attr) as any))
            setValue('url', getProfileValue(profile, 'dbmarlin.url') as string)
            setValue('ssl', getProfileValue(profile, 'starttls.enable') === true)
            void trigger()
        }
    }

    useEffect(() => {
        parseEditResponse(props.emailIntegration)
    }, [props.emailIntegration, action.type, profileId]);

    const { dirtyFields } = useFormState({
        control
    })

    const onSubmit = async (data: EmailService) => {
        setDisabled(true)
        let updatedValues: { name: string; value: any; }[] = []
        let result: any;
        let url = `/parameter`;
        const prefix = action.type === ACTIONS.CREATE.type
            ? getNextProfilePrefix(props.emailIntegration, config)
            : profileId || config.basePrefix;

        if (action.type === ACTIONS.DELETE.type) {
            const profile = findProfile(props.emailIntegration, config, prefix);
            result = await api.delete(url, { data: (profile?.parameters ?? []).map(parameter => ({ name: parameter.name })) })
        } else {
            Object.keys(data).forEach(item => {
                // Never persist credentials when authentication is disabled.
                if (!authEnabled && (item === 'user' || item === 'password')) {
                    return;
                }
                let name = item === 'ssl' ? `${prefix}.starttls.enable` : `${prefix}.${item}`
                name = item === 'url' ? `${prefix}.dbmarlin.url` : name;
                name = item === 'name' ? `${prefix}.dbmarlin.name` : name;
                // @ts-ignore
                const value = data[item] === true ? 'true' : data[item] === false ? 'false' : data[item]
                if (action.type === 'create') {
                    updatedValues.push({ name, value })
                    // @ts-ignore
                } else if (dirtyFields[item]) {
                    updatedValues.push({ name, value })
                }
            })

            if (!authEnabled) {
                // Remove any stored credentials so the e-mail is sent without
                // SMTP authentication. Storing blank values would make the mail
                // library authenticate with empty credentials, which open
                // (unauthenticated) relays reject.
                const profile = findProfile(props.emailIntegration, config, prefix);
                const credentials = (profile?.parameters ?? [])
                        .filter(parameter => parameter.name === `${prefix}.user`
                                || parameter.name === `${prefix}.password`)
                        .map(parameter => ({ name: parameter.name }));
                if (credentials.length > 0) {
                    await api.delete(url, { data: credentials })
                }
            }

            result = updatedValues.length > 0 ? await api.post(url, updatedValues) : { status: 200 }
        }

        if (result.status === 200) {
            navigate('/admin/integrations/email')
            props.refresh()
        }
    }
    const isValid = Object.keys(errors).length === 0
    const authEnabled = watch('auth');

    useEffect(() => {
        if (!authEnabled) {
            // Reset errors if disabling auth
            clearErrors(["user", "password"]);
        }
    }, [authEnabled]);

    return (
            <div className="col">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-envelope fa-fw mr-10" aria-hidden="true"/>
                        {action.title} Email Settings
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit(onSubmit)}>

                            {!isValid && (
                                    <Alert message={generalErrorMessage.text} heading={generalErrorMessage.title}
                                           variant="alert-danger"/>
                            )}

                            <p>Please complete the required details below:</p>

                            <div className="row g-0 form">
                                <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="name" className="base-url">Name</label>
                                </span>
                                </div>

                                <div className="col-xs-12 col-md-6 col-lg-4">
                                    <input
                                            type="text"
                                            id="name"
                                            maxLength={250}
                                            {...register("name" as keyof EmailService, { required: "Please fill in this field" })}
                                            className={`form-control ${getValidClass((errors as any).name, getValues('name' as keyof EmailService) as any, isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            title={(errors as any).name?.message}
                                            data-lpignore={true}
                                    />
                                    <small className="text-danger">{(errors as any).name?.message}</small>
                                </div>
                                <div className="w-100"/>

                                {/* Base URL */}
                                <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="url" className="base-url">DBmarlin base URL</label>
                                </span>
                                </div>

                                <div className="col-xs-12 col-md-6 col-lg-4">
                                    <input
                                            type="text"
                                            id="url"
                                            maxLength={250}
                                            {...register("url", { required: "Please fill in this field" })}
                                            className={`form-control ${getValidClass(errors.url, getValues('url'), isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            title={errors.url?.message}
                                            data-lpignore={true}
                                    />
                                    <small className="text-danger">{errors.url?.message}</small>
                                </div>
                                <div className="w-100"/>

                                {/* Host */}
                                <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="host" className="reminder">SMTP Host</label>
                                </span>
                                </div>

                                <div className="col-xs-12 col-md-6 col-lg-4">
                                    <input
                                            type="text"
                                            id="host"
                                            maxLength={250}
                                            {...register("host", { required: "Please fill in this field" })}
                                            className={`form-control ${getValidClass(errors.host, getValues('host'), isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            title={errors.host?.message}
                                            data-lpignore={true}
                                    />
                                    <small className="text-danger">{errors.host?.message}</small>
                                </div>
                                <div className="w-100"/>

                                {/* Port */}
                                <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="port" className="reminder">SMTP Port</label>
                                </span>
                                </div>

                                <div className="col-xs-12 col-md-6 col-lg-4">
                                    <input
                                            type="text"
                                            id="port"
                                            maxLength={250}
                                            {...register("port", { required: "Please fill in this field" })}
                                            className={`form-control ${getValidClass(errors.port, getValues('port'), isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            title={errors.port?.message}
                                            data-lpignore={true}
                                    />
                                    <small className="text-danger">{errors.port?.message}</small>
                                </div>
                                <div className="w-100"/>

                                {/* SSL enabled */}
                                <div className="col-xs-12 col-md-3">
                                    <label className="auth">SSL enabled</label>
                                </div>
                                <div className="col-xs-12 col-md-6 col-lg-3">
                                    <div className='form-check form-switch'>
                                        <input
                                                type="checkbox"
                                                id="ssl"
                                                {...register("ssl")}
                                                disabled={disabled}
                                                className="form-check-input"
                                                // onChange={() => setValue('ssl', !getValues('ssl'))}
                                                defaultChecked={true}
                                                data-lpignore={true}
                                        />
                                    </div>
                                </div>
                                <div className="w-100"/>

                                {/* Auth enabled */}
                                <div className="col-xs-12 col-md-3">
                                    <label className="auth">Authentication</label>
                                </div>
                                <div className="col-xs-12 col-md-6 col-lg-3">
                                    <div className='form-check form-switch'>
                                        <input
                                                type="checkbox"
                                                id="auth"
                                                {...register("auth")}
                                                className="form-check-input"
                                                disabled={disabled}
                                                defaultChecked={true}
                                                // onChange={() => setValue('auth', !getValues('auth'))}
                                                data-lpignore={true}
                                        />
                                    </div>
                                </div>
                                <div className="w-100"/>
                                <ConditionalRender if={authEnabled}>
                                    {/* Username */}
                                    <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="user" className="reminder">SMTP Username</label>
                                </span>
                                    </div>

                                    <div className="col-xs-12 col-md-6 col-lg-4">
                                        <input
                                                type="text"
                                                id="user"
                                                maxLength={250}
                                                {...register("user", {
                                                    validate: () =>
                                                            !authEnabled || !!getValues("user") || "Please fill in this field"
                                                })}
                                                className={`form-control ${getValidClass(errors.user, getValues('user'), isDelete)}`}
                                                defaultValue=''
                                                disabled={disabled}
                                                autoComplete="off"
                                                title={errors.user?.message}
                                                data-lpignore={true}
                                        />
                                        <small className="text-danger">{errors.user?.message}</small>
                                    </div>
                                    <div className="w-100"/>

                                    {/* Password */}
                                    <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="password" className="reminder">SMTP Password</label>
                                </span>
                                        {visiblePassword && (
                                                <i className="fas fa-eye fa-fw fa-xs ms-1" role="button"
                                                   onClick={() => ((smtpPassElementType === 'text') ? setSmtpPassElementType('password') : setSmtpPassElementType('text'))}/>
                                        )}
                                    </div>

                                    <div className="col-xs-12 col-md-6 col-lg-4">
                                        <input
                                                type={smtpPassElementType}
                                                id="password"
                                                maxLength={250}
                                                {...register("password", {
                                                    validate: () =>
                                                            !authEnabled || !!getValues("password") || "Please fill in this field"
                                                })}
                                                className={`form-control ${getValidClass(errors.password, getValues('password'), isDelete)}`}
                                                defaultValue=''
                                                disabled={disabled}
                                                autoComplete="new-password"
                                                title={errors.password?.message}
                                                data-lpignore={true}
                                                onChange={handleChange}
                                        />
                                        <small className="text-danger">{errors.password?.message}</small>
                                    </div>
                                    <div className="w-100"/>
                                </ConditionalRender>

                                {/* SMTP From Address */}
                                <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="from" className="reminder">SMTP From Address</label>
                                </span>
                                </div>

                                <div className="col-xs-12 col-md-6 col-lg-4">
                                    <input
                                            type="text"
                                            id="from"
                                            maxLength={250}
                                            {...register("from", {
                                                required: "Please fill in this field"
                                            })}
                                            className={`form-control ${getValidClass(errors.from, getValues('from'), isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            title={errors.from?.message}
                                            data-lpignore={true}
                                    />
                                    <small className="text-danger">{errors.from?.message}</small>
                                </div>
                                <div className="w-100"/>

                                {/* SMTP To Address */}
                                <div className="col-xs-12 col-md-3">
                                <span className="required">
                                    <label htmlFor="to" className="reminder">SMTP To Address</label>
                                </span>
                                </div>

                                <div className="col-xs-12 col-md-6 col-lg-4">
                                    <input
                                            type="text"
                                            id="to"
                                            maxLength={250}
                                            {...register("to", {
                                                required: "Please fill in this field"
                                            })}
                                            className={`form-control ${getValidClass(errors.to, getValues('to'), isDelete)}`}
                                            defaultValue=''
                                            disabled={disabled}
                                            title={errors.to?.message}
                                            data-lpignore={true}
                                    />
                                    <small className="text-danger">{errors.to?.message}</small>
                                </div>
                                <div className="w-100"/>

                                {/*Email  enabled */}
                                <div className="col-xs-12 col-md-3">
                                    <label className="enabled">Email enabled</label>
                                </div>
                                <div className="col-xs-12 col-md-6 col-lg-3">
                                    <div className='form-check form-switch'>
                                        <input
                                                type="checkbox"
                                                id="enable"
                                                {...register("enable")}
                                                className="form-check-input"
                                                disabled={disabled}
                                                defaultChecked={true}
                                                data-lpignore={true}
                                        />
                                    </div>
                                </div>
                                <div className="w-100"/>

                                {/* Actions */}
                                <div className="col-12 actions">
                                    <Link to="/admin/integrations/email" role="button" className="btn btn-secondary"><i
                                            className="fal fa-times-circle fa-fw fa-sm"/><span>Cancel</span></Link>
                                    {action.type === 'update' && <TestNowButton className="btn btn-info"/>}
                                    <button type="submit" className={`btn btn-${action.variant}`}><i
                                            className={`${action.icon} fa-sm`}/><span>{action.title}</span></button>
                                </div>
                                <TestResultAlert className="col-12"/>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
    )
}

export default Detail;
